/**
 * <s-audio-clip> — Single-clip audio player with play/pause, progress and time.
 *
 * Only one clip plays at a time: starting one pauses every other <s-audio-clip>.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --toggle — Play or pause the clip.
 *
 * Required child markup:
 *   audio                   — The native audio element (src set by Liquid).
 *   [data-audio-toggle]     — The play/pause button; aria-pressed and aria-label are kept in sync.
 *                             data-label-play / data-label-pause hold the translated labels.
 *   [data-audio-progress]   — (optional) Element whose inline width tracks playback (0–100%).
 *   [data-audio-time]       — (optional) Text node target for "elapsed / duration".
 *
 * State: sets [data-playing] on the host while audio is playing.
 */
const clips = new Set();

class SAudioClip extends HTMLElement {
  #ac;
  #audio;
  #toggle;
  #progress;
  #time;
  #observer;

  connectedCallback() {
    this.#audio = this.querySelector('audio');
    this.#toggle = this.querySelector('[data-audio-toggle]');
    this.#progress = this.querySelector('[data-audio-progress]');
    this.#time = this.querySelector('[data-audio-time]');
    if (!this.#audio || !this.#toggle) return;

    clips.add(this);
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command === '--toggle') this.#togglePlayback();
    }, opts);

    this.#audio.addEventListener('play', () => this.#onPlay(), opts);
    this.#audio.addEventListener('pause', () => this.#setPlaying(false), opts);
    this.#audio.addEventListener('ended', () => {
      this.#setPlaying(false);
      this.#audio.currentTime = 0;
      this.#render();
    }, opts);
    this.#audio.addEventListener('timeupdate', () => this.#render(), opts);
    this.#audio.addEventListener('loadedmetadata', () => this.#render(), opts);

    // Pause when the clip is hidden (e.g. the scene switcher swaps panels) or scrolled away.
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) this.#audio.pause();
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    clips.delete(this);
    this.#audio?.pause();
  }

  pause() {
    this.#audio?.pause();
  }

  #togglePlayback() {
    if (this.#audio.paused) {
      const attempt = this.#audio.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => this.#setPlaying(false));
    } else {
      this.#audio.pause();
    }
  }

  #onPlay() {
    for (const clip of clips) {
      if (clip !== this) clip.pause();
    }
    this.#setPlaying(true);
  }

  #setPlaying(isPlaying) {
    this.toggleAttribute('data-playing', isPlaying);
    this.#toggle.setAttribute('aria-pressed', String(isPlaying));
    const label = isPlaying ? this.#toggle.dataset.labelPause : this.#toggle.dataset.labelPlay;
    if (label) this.#toggle.setAttribute('aria-label', label);
  }

  #render() {
    const duration = Number.isFinite(this.#audio.duration) ? this.#audio.duration : 0;
    const elapsed = this.#audio.currentTime || 0;
    if (this.#progress) {
      const percent = duration > 0 ? Math.min(100, (elapsed / duration) * 100) : 0;
      this.#progress.style.width = `${percent}%`;
    }
    if (this.#time && duration > 0) {
      this.#time.textContent = `${this.#format(elapsed)} / ${this.#format(duration)}`;
    }
  }

  #format(seconds) {
    const whole = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(whole / 60);
    const rest = String(whole % 60).padStart(2, '0');
    return `${minutes}:${rest}`;
  }
}

if (!customElements.get('s-audio-clip')) customElements.define('s-audio-clip', SAudioClip);
